import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navigation from '../components/Navigation';
import { getAuthHeaders } from '../lib/auth';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Sparkles, Clock, Calendar, Crown, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DURATIONS = [
  { value: 30, label: '30 seconds' },
  { value: 45, label: '45 seconds' },
  { value: 60, label: '60 seconds' }
];

export default function PresentorNew() {
  const navigate = useNavigate();
  const [hasConfig, setHasConfig] = useState(false);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    businessName: '',
    chapterName: 'Business Chapter',
    meetingDay: 'Monday',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    aboutBusiness: '',
    specificAsks: 'I am looking for referrals to:\n- [Client type 1]\n- [Client type 2]\n- [Client type 3]',
    duration: 60
  });

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/presentor/config`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.exists) {
        setHasConfig(true);
        const configData = response.data.config;
        setConfig(configData);
        
        // Populate form with existing config data
        setForm({
          name: configData.name || '',
          businessName: configData.businessName || '',
          chapterName: configData.chapterName || 'Business Chapter',
          meetingDay: configData.meetingDay || 'Monday',
          timezone: configData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          aboutBusiness: configData.aboutBusiness || '',
          specificAsks: configData.specificAsks || 'I am looking for referrals to:\n- [Client type 1]\n- [Client type 2]\n- [Client type 3]',
          duration: configData.duration || 60
        });
      }
    } catch (error) {
      console.error('Failed to load config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleGenerate = async () => {
    if (!form.name || !form.businessName || !form.aboutBusiness) {
      toast.error('Please fill in all required fields: Name, Business Name, and About Business');
      return;
    }

    if (!form.specificAsks || form.specificAsks.trim() === '') {
      toast.error('Please fill in your Specific Asks / Referral Requests');
      return;
    }

    try {
      setGenerating(true);
      console.log('Sending generate request with form data:', form);
      
      const response = await axios.post(
        `${API}/presentor/generate`,
        form,
        { headers: getAuthHeaders() }
      );

      console.log('Generate response:', response.data);
      
      // Immediately update state with the generated config
      const newConfig = {
        ...form,
        userId: response.data.userId,
        scripts: response.data.scripts,
        generationCount: response.data.generationCount,
        remainingFreeGenerations: response.data.remainingFreeGenerations,
        nextGenerationDate: response.data.nextGenerationDate,
        currentWeek: 1,
        isPro: false,
        lastGeneratedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };
      
      setConfig(newConfig);
      setHasConfig(true);
      
      toast.success(response.data.message || '4 weeks of scripts generated successfully!');
      
      if (response.data.remainingFreeGenerations === 0 && !config?.isPro) {
        toast.warning('You have used all free generations. Upgrade to Pro for unlimited!');
      } else if (response.data.remainingFreeGenerations > 0) {
        toast.info(`${response.data.remainingFreeGenerations} free generation(s) remaining`);
      }
      
      // Also reload from server to ensure sync
      await loadConfig();
    } catch (error) {
      console.error('Failed to generate:', error);
      console.error('Error response:', error.response);
      
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Generation limit reached. Please upgrade to Pro.');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to generate scripts: ${error.message}`);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleUpgradeToPro = async () => {
    try {
      await axios.post(
        `${API}/presentor/upgrade-pro`,
        {},
        { headers: getAuthHeaders() }
      );
      
      // Immediately update state
      if (config) {
        setConfig({ ...config, isPro: true });
      }
      
      toast.success('Upgraded to Pro! Unlimited regenerations unlocked.');
      await loadConfig();
    } catch (error) {
      console.error('Failed to upgrade:', error);
      toast.error('Failed to upgrade to Pro');
    }
  };

  const copyScript = (script) => {
    navigator.clipboard.writeText(script);
    toast.success('Script copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="p-6 flex items-center justify-center">
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AI Presentor</h1>
              <p className="text-gray-600">4-week networking presentation scripts with auto-notifications</p>
            </div>
          </div>
          
          {hasConfig && (
            <div className="flex items-center gap-2">
              {config?.isPro ? (
                <div className="flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg">
                  <Crown className="w-5 h-5" />
                  <span className="font-semibold">Pro Member</span>
                </div>
              ) : (
                <Button onClick={handleUpgradeToPro} variant="outline" className="flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Upgrade to Pro
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Configuration Form */}
        <Card className="max-w-4xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              {hasConfig ? 'Your Presentation Configuration' : 'Setup Your Networking Presentation'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Your Name *</label>
                <Input
                  placeholder="John Smith"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Business Name *</label>
                <Input
                  placeholder="Tech Solutions Inc"
                  value={form.businessName}
                  onChange={(e) => setForm({ ...form, businessName: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Chapter Name</label>
                <Input
                  placeholder="Hawaii Chapter"
                  value={form.chapterName}
                  onChange={(e) => setForm({ ...form, chapterName: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Meeting Day *</label>
                <Select value={form.meetingDay} onValueChange={(value) => setForm({ ...form, meetingDay: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map(day => (
                      <SelectItem key={day} value={day}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Presentation Duration *</label>
                <Select value={String(form.duration)} onValueChange={(value) => setForm({ ...form, duration: Number(value) })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map(dur => (
                      <SelectItem key={dur.value} value={String(dur.value)}>{dur.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Your Timezone</label>
                <Input
                  placeholder="America/New_York"
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Auto-detected: {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">About Your Business *</label>
              <Textarea
                placeholder="We help businesses automate their workflows and increase productivity..."
                value={form.aboutBusiness}
                onChange={(e) => setForm({ ...form, aboutBusiness: e.target.value })}
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Specific Asks / Referral Requests *</label>
              <Textarea
                placeholder="I am looking for referrals to..."
                value={form.specificAsks}
                onChange={(e) => setForm({ ...form, specificAsks: e.target.value })}
                rows={4}
              />
            </div>

            <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">What happens after generation:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>AI generates 4 unique weekly scripts (no repetition)</li>
                  <li>Email & notification sent <strong>day before</strong> your meeting</li>
                  <li>Email & notification sent <strong>morning of (6 AM)</strong> your meeting</li>
                  <li>Auto-regenerates after 30 days</li>
                  <li><strong>2 free regenerations</strong>, then upgrade to Pro for unlimited</li>
                </ul>
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              size="lg"
            >
              {generating ? (
                <>
                  <Clock className="w-5 h-5 mr-2 animate-spin" />
                  Generating 4 Weeks of Scripts...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate 4-Week Presentation Scripts
                </>
              )}
            </Button>

            {hasConfig && config && (
              <div className="grid sm:grid-cols-3 gap-3 pt-4 border-t">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">Generations Used</p>
                  <p className="text-2xl font-bold text-gray-900">{config.generationCount}</p>
                  {!config.isPro && <p className="text-xs text-gray-500">{Math.max(0, 2 - config.generationCount)} free left</p>}
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">Current Week</p>
                  <p className="text-2xl font-bold text-indigo-600">Week {config.currentWeek}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">Next Regeneration</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {config.nextGenerationDate ? new Date(config.nextGenerationDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Display Scripts */}
        {hasConfig && config?.scripts && config.scripts.length > 0 && (
          <div className="space-y-4 max-w-4xl">
            <h2 className="text-2xl font-bold text-gray-900">Your 4-Week Presentation Scripts</h2>
            
            {config.scripts.map((script) => (
              <Card key={script.week} className={script.week === config.currentWeek ? 'border-2 border-indigo-600' : ''}>
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-lg">
                      Week {script.week}
                      {script.week === config.currentWeek && (
                        <span className="ml-2 text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">Current</span>
                      )}
                    </CardTitle>
                    <span className="text-sm text-gray-500">{script.duration}s</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyScript(script.script)}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </CardHeader>
                <CardContent>
                  <pre className="whitespace-pre-wrap text-sm bg-gray-50 p-4 rounded-lg border font-sans">
                    {script.script}
                  </pre>
                  <p className="text-xs text-gray-500 mt-2">
                    Generated: {new Date(script.generatedAt).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            ))}
            
            {/* Create Poster Section */}
            <Card className="mt-6 border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-red-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Create Social Media Poster</h3>
                    <p className="text-sm text-gray-600">
                      Use Digi Strategy to create professional marketing posters with full Canva-style editor
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/digi-strategy')}
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
                    size="lg"
                  >
                    <ImageIcon className="w-5 h-5 mr-2" />
                    Go to Digi Strategy
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
